import { useCallback, useContext, useEffect } from 'react'
import { CrmAccessTokenPayloadDto, parseJwt, WebAccessTokenPayloadDto } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { loginFailure, setAuthToken, unsetAuthToken } from '../../store/actions'
import { AppStateContext } from './AppStateContext'
import { authTokenSelector } from '../../store/selectors/app'

interface AuthResult {
  saveToken: (token: string | undefined) => void
  removeToken: () => void
  isUserLoggedIn: boolean
  getAccessTokenDataFromAuthToken: (token: string | undefined) => CrmAccessTokenPayloadDto | undefined
}

export const useAuth: () => AuthResult = () => {
  const { setAuthTokenInStore, removeAuthTokenFromStore, isLoggedIn, getAuthToken } = useContext(AppStateContext)
  const dispatch = useDispatch()
  const storedAuthToken = useSelector(authTokenSelector)

  const getAccessTokenDataFromAuthToken = (token: string | undefined): WebAccessTokenPayloadDto | undefined => {
    if (token) {
      return <WebAccessTokenPayloadDto>parseJwt(token)
    }

    return undefined
  }

  useEffect(() => {
    const token = getAuthToken()
    if (token && !storedAuthToken) {
      dispatch(setAuthToken(token))
    }
  }, [dispatch, getAuthToken, storedAuthToken])

  const saveToken = useCallback(
    (token: string | undefined) => {
      if (!storedAuthToken) {
        dispatch(setAuthToken(token as string))
      }

      return setAuthTokenInStore(token)
    },
    [dispatch, setAuthTokenInStore, storedAuthToken],
  )

  const removeToken = useCallback(() => {
    removeAuthTokenFromStore()
    dispatch(unsetAuthToken())
    dispatch(loginFailure(null))
  }, [dispatch, removeAuthTokenFromStore])

  const isUserLoggedIn = isLoggedIn

  return {
    saveToken,
    removeToken,
    isUserLoggedIn,
    getAccessTokenDataFromAuthToken,
  }
}
